import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Home,
  LogOut,
  Settings,
  User,
} from 'lucide-react';

import type { ComponentType } from 'react';

import { Logo, LogoIcon } from '@/components/brand/Logo';
import { chromeTokens, type Surface } from '@/components/layout/surface';
import { useAuth } from '@/lib/auth/useAuth';
import { roleLabel } from '@/lib/nav/roleNav';
import { freemiumQueries } from '@/lib/queries/freemium';
import { subscriptionQueries } from '@/lib/queries/subscription';
import { cn } from '@/lib/utils/cn';
import { useUIStore } from '@/stores/ui';

export interface SidebarNavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  disabled?: boolean;
  disabledTooltip?: string;
}

export interface SidebarNavGroup {
  label?: string;
  items: SidebarNavItem[];
}

interface SidebarProps {
  groups: SidebarNavGroup[];
  homeHref: string;
  accentClass?: string;
  surface?: Surface;
  /**
   * The tool-exit affordance (Spec 28 A4): a distinct "Home" link back to the
   * reader home, rendered above the tool's own nav with a divider so it reads as
   * leaving the tool. Omitted on the reader shell (you're already home).
   */
  exitLink?: { to: string; label: string };
}

export function Sidebar({
  groups,
  homeHref,
  accentClass,
  surface = 'editorial',
  exitLink,
}: SidebarProps) {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggle = useUIStore((s) => s.toggleSidebar);
  const { profile, session, signOut } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const t = chromeTokens[surface].sidebar;

  // Footer identity badge (Spec 27). Founder + membership live on the
  // subscription, not the profile. Founder outranks every label, so read the
  // subscription for any signed-in user (cached — subscriber.index already
  // fetches it on the reader shell; one light read on staff shells). Membership
  // only decides the plain-member badge, so fetch the canonical freemium status
  // (shared with JoinCta) only when nothing higher applies.
  const signedIn = Boolean(session);
  const { data: sub } = useQuery(subscriptionQueries.current(session?.user.id));
  const isFounder = sub?.is_founder ?? false;
  const higher = isFounder || roleLabel(profile) !== null;
  const { data: freemium } = useQuery(freemiumQueries.status(signedIn && !higher));
  const label = roleLabel(profile, { isFounder, isMember: freemium?.isMember ?? false });
  const marketingUrl = import.meta.env.PUBLIC_MARKETING_URL;

  return (
    <motion.aside
      animate={{ width: collapsed ? 72 : 240 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className={cn(
        'sticky top-0 hidden h-screen flex-col border-r transition-colors md:flex',
        t.aside,
        accentClass,
      )}
      aria-label="Primary"
    >
      <div className="flex items-center justify-between p-4">
        {/* The wordmark is the public front door (Spec 28 A2): it opens the
            marketing front page from every shell, so "the logo goes home" holds
            identically everywhere. Home base inside the app is "Home". */}
        <a href={marketingUrl} className="flex items-center" aria-label="ArcheTelos — public site">
          {collapsed ? <LogoIcon className="h-8 w-8" /> : <Logo className="h-7 w-auto" />}
        </a>
        <button
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={cn('rounded-md p-1', t.collapseBtn)}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-2 py-2">
        {exitLink && (
          <div className={cn('mb-1 border-b pb-2', t.footerBorder)}>
            <Link
              to={exitLink.to}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                t.item,
              )}
              aria-label={`${exitLink.label} — leave this tool`}
            >
              <Home className="h-4 w-4 shrink-0" />
              {!collapsed && <span className="truncate">{exitLink.label}</span>}
            </Link>
          </div>
        )}
        {groups.map((group, gi) => (
          <div key={group.label ?? `group-${gi}`}>
            {group.label && !collapsed && (
              <p
                className={cn(
                  'px-3 pb-1 font-sans text-[0.65rem] font-semibold uppercase tracking-[0.14em]',
                  t.sectionLabel,
                )}
              >
                {group.label}
              </p>
            )}
            <ul className="space-y-1">
              {group.items.map((item) => {
                // The layout root (== homeHref) matches exactly; everything else
                // prefix-matches so child routes keep their parent active.
                const active =
                  item.to === homeHref
                    ? pathname === item.to
                    : pathname === item.to || pathname.startsWith(item.to + '/');
                const Icon = item.icon;
                const content = (
                  <span
                    className={cn(
                      'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      active ? t.itemActive : t.item,
                      item.disabled && 'pointer-events-none opacity-40',
                    )}
                  >
                    {active && (
                      <span
                        aria-hidden
                        className={cn(
                          'absolute bottom-1 left-0 top-1 w-[3px] rounded-r-full',
                          t.itemBar,
                        )}
                      />
                    )}
                    <Icon className="h-4 w-4 shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                  </span>
                );
                return (
                  <li key={item.to}>
                    {item.disabled ? (
                      <span className="block" title={item.disabledTooltip} aria-disabled="true">
                        {content}
                      </span>
                    ) : (
                      <Link to={item.to} aria-current={active ? 'page' : undefined}>
                        {content}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className={cn('border-t p-3', t.footerBorder)}>
        <Link
          to="/subscriber/settings"
          className={cn('flex items-center gap-3 rounded-md px-2 py-2 text-sm', t.footerLink)}
        >
          {profile?.avatar_url ? (
            <img src={profile.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <span
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full',
                t.avatarFallback,
              )}
            >
              <User className="h-4 w-4" />
            </span>
          )}
          {!collapsed && (
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{profile?.display_name ?? 'Account'}</span>
              {label && (
                <span className={cn('truncate font-sans text-[0.7rem] leading-tight', t.roleBadge)}>
                  {label}
                </span>
              )}
            </span>
          )}
        </Link>
        <div className="mt-1 grid gap-1">
          <Link
            to="/subscriber/settings"
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-1.5 text-xs',
              t.footerSubLink,
            )}
          >
            <Settings className="h-3.5 w-3.5" />
            {!collapsed && <span>Account</span>}
          </Link>
          {marketingUrl && (
            <a
              href={marketingUrl}
              target="_blank"
              rel="noreferrer"
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-1.5 text-xs',
                t.footerSubLink,
              )}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {!collapsed && <span>View public site</span>}
            </a>
          )}
          <button
            type="button"
            onClick={() => void signOut()}
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-1.5 text-xs',
              t.footerSubLink,
            )}
          >
            <LogOut className="h-3.5 w-3.5" />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </div>
    </motion.aside>
  );
}
